import type { DashboardKpis, SalesIndicator } from '@sdr-agend/admin-schemas'

/**
 * `salesIndicator.valueSum / salesIndicator.count`. `null` when there are no
 * sales yet in the period (avoids a `0/0` NaN — same "N/A, not a misleading
 * number" convention used across the dashboard). Extracted from
 * `CommercialResultsPanel.tsx` (2026-08-17, user request via chamado) so the
 * top KPI row can reuse the exact same formula instead of a copy.
 */
/**
 * 2026-08-24 (user request) — uses `totalCount`/`totalValueSum` (all synced
 * GHL sales), not `count`/`valueSum` (IA/campaign-attributed only), so the
 * average ticket reflects the real business, matching the "Vendas (total
 * GHL)"/"Faturamento (total GHL)" rows this sits next to in the panel.
 */
export function ticketMedio(salesIndicator: SalesIndicator): number | null {
  return salesIndicator.totalCount > 0 ? salesIndicator.totalValueSum / salesIndicator.totalCount : null
}

/**
 * 2026-08-18 (QA fix, Quinn @qa) — `kpis.meetingsScheduled` alone is only
 * `outcome IN ('pending', 'confirmed')` (see `queryKpiCounts` in
 * `routes/admin/dashboard.ts`) — reuniões AINDA EM ABERTO, não o total
 * agendado no período. Reusar esse campo sozinho como "Agendamentos" no
 * topo do painel subestimava o número (reuniões já compareceu/faltou/
 * cancelada/sem resposta ao lembrete saem da contagem), quebrando a leitura
 * de funil "Agendamentos → Realizadas → Vendas" pedida pela Haislann.
 *
 * Soma os 5 status de `meetings.outcome` que representam uma reunião
 * REALMENTE agendada no período (`pending`+`confirmed` via
 * `meetingsScheduled`, `attended`, `no_show`, `cancelled`, `unknown`).
 * Deliberadamente NÃO soma `rescheduled` — quando uma reunião é reagendada, a
 * linha ANTIGA é marcada `outcome='rescheduled'` e uma linha NOVA é criada
 * pra nova data (`meetings.repo.ts`'s `markRescheduled`); somar as duas
 * contaria a mesma reunião lógica duas vezes.
 */
export function totalMeetingsScheduled(kpis: DashboardKpis): number {
  return (
    kpis.meetingsScheduled +
    kpis.meetingsAttended +
    kpis.meetingsNoShow +
    kpis.meetingsCancelled +
    kpis.meetingsUnknown
  )
}

/**
 * `kpis.attendanceRate` is already `attended / (attended + noShow)` — the
 * complement is the no-show rate, no extra backend field needed. Extracted
 * from `CommercialResultsPanel.tsx` (2026-08-17, user request via chamado).
 */
export function noShowRate(attendanceRate: number | null): number | null {
  return attendanceRate === null ? null : 1 - attendanceRate
}
